import React, { FC, useEffect } from 'react';

import { useSelector, useDispatch } from 'react-redux';
import { RootState, Dispatch } from '../../../store';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';

import Typography from '../../../components/Typography/Typography';
import Button from '../../../components/Button/Button';
import ASSETS from '../../../assets';
import { ITEMS } from '../Documents';
import { ClipLoader } from 'react-spinners';

dayjs.extend(relativeTime);
dayjs.extend(utc);

interface IProps {
  setTab: React.Dispatch<React.SetStateAction<ITEMS>>;
  searchTerm?: string;
  isSearched?: boolean;
}

const KPMGScan: FC<IProps> = ({ setTab, searchTerm = '', isSearched = false }) => {
  const dispatch = useDispatch<Dispatch>();

  const { KPMGScan, loading } = useSelector(
    (state: RootState) => state.attesation
  );
  
  // Client-side search by title/UID for KPMG Scan
  const getFilteredKPMGScan = (scans: any[]) => {
    if (!searchTerm.trim() || !isSearched) {
      return scans;
    }
    
    return scans.filter(scan => 
      scan.UID?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      scan.creator?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };
  
  const finalKPMGScans = getFilteredKPMGScan(KPMGScan || []);
  const handleViewAllAttestations = () => {
    setTab('all-attesations');
    // Scroll to the top of the page when switching tabs
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    dispatch.attesation.handleGetKPMGScan();
    // eslint-disable-next-line
  }, []);

  if (loading) {
    return (
      <div className='flex items-center justify-center w-full min-h-screen'>
        <ClipLoader size={40} color='#01032C' />
      </div>
    );
  }
  return (
    <div>
      {finalKPMGScans && finalKPMGScans.length > 0 ? (
        <>
          <div className='w-full overflow-auto'>
            <table className='w-full border-collapse'>
              <thead>
                <tr className='[&_th]:text-sm [&_th]:text-k-grey-text [&_th]:font-medium [&_th]:text-left [&_th]:border-b [&_th]:border-b-k-border [&_th]:h-[37px]'>
                  <th className='pl-[15px]'>UID</th>
                  <th>Schema</th>
                  <th>Creator</th>
                  <th>Location</th>
                  <th className='pr-[12px] pl-[6px]'>Age</th>
                </tr>
              </thead>
              <tbody>
                {finalKPMGScans.map(attestation => (
                  <tr
                    key={attestation.UID}
                    className='[&_td]:h-[55px] [&_td]:border-b [&_td]:border-b-k-border [&:last-of-type_td]:border-none'
                  >
                    <td className='pl-[15px]'>
                      <a
                        href={attestation.verifyOnEAS}
                        rel='noopener noreferrer'
                        target='_blank'
                      >
                        <Typography className='truncate w-[27ch] text-k-dark-blue'>
                          {attestation.UID}
                        </Typography>
                      </a>
                    </td>
                    <td>
                      <div className='flex'>
                        <div className='bg-k-dark-blue text-sm text-white px-[5px] py-[2px] rounded-l-[4px]'>
                          #145
                        </div>
                        <div className='text-sm text-k-blue semibold px-[5px] py-[1px] border border-k-light-blue-300 bg-[#D4EBFF] rounded-r'>
                          Endorsements
                        </div>
                      </div>
                    </td>
                    <td>
                      <a
                        href={`https://base-sepolia.blockscout.com/address/${attestation.creator}`}
                        rel='noopener noreferrer'
                        target='_blank'
                      >
                        <Typography className='truncate w-[24ch] text-k-dark-blue'>
                          {attestation.creator}
                        </Typography>
                      </a>
                    </td>
                    <td>
                      <div className='flex items-center gap-1'>
                        <img 
                          src={attestation.type === 'On Chain' ? ASSETS.onchain : ASSETS.offchain} 
                          alt={attestation.type === 'On Chain' ? 'On Chain' : 'Off Chain'} 
                          className='w-4 h-4'
                        />
                        <Typography variant='h6' color='grey'>
                          {attestation.type}
                        </Typography>
                      </div>
                    </td>
                    <td className='pl-[6px]'>
                      <Typography variant='h6' color='grey' className=''>
                        {dayjs.unix(+attestation.updatedTime).utc().fromNow()}
                      </Typography>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!isSearched && (
            <div className='flex justify-center mt-10'>
              <Button
                label='View All Attestations'
                leftIcon={ASSETS.add_black}
                onClick={handleViewAllAttestations}
              />
            </div>
          )}
        </>
      ) : (
        <div className='flex-grow h-1 min-h-[300px]'>
          <div className='h-full bg-k-grey-bg border border-k-border rounded-[12px] flex flex-col items-center justify-center'>
            <Typography variant='h4'>
              {isSearched ? 'No Attestation Found' : 'No Attestation Found'}
            </Typography>
          </div>
        </div>
      )}
    </div>
  );
};

export default KPMGScan;
